import { ApplicationConfig, provideBrowserGlobalErrorListeners, provideZonelessChangeDetection } from '@angular/core';
import { IMAGE_CONFIG } from '@angular/common';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideRouter, withComponentInputBinding, withRouterConfig } from '@angular/router';
import { CORE_INTERCEPTORS, provideCore } from '@corelabs/angular/core';
import { provideAccount } from '@corelabs/angular/account';
import { provideMessages } from '@corelabs/angular/messages';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';

import { environment } from '@env/environment';
import { DEVTOOLS_PROVIDERS } from '@env/devtools';

import { apiErrorMessageInterceptor, HttpService, timeoutInterceptor } from './core/http';
import { metricsInterceptor } from './core/metrics';
import { appRoutes } from './app.routing';
import { MissingTranslationHandler, provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { customMissingTranslationHandler, translateLoaderStatic } from './i18n';
import { provideIconStore } from './store/icon';
import { provideSessionStore } from './store/session';
import { clearState } from './store/clear-state.reducer';

export const appConfig: ApplicationConfig = {
    providers: [
        provideBrowserGlobalErrorListeners(),
        provideZonelessChangeDetection(),
        // Angular's dev-only ImagePerformanceWarning calls PerformanceObserver.observe({ type })
        // and largest-contentful-paint, neither of which exists in the Sunmi L2s WebView — it
        // throws before appRef.bootstrap(), so nothing renders. Both flags together make it
        // return early. The app uses no NgOptimizedImage, so the warnings could never fire.
        { provide: IMAGE_CONFIG, useValue: { disableImageSizeWarning: true, disableImageLazyLoadWarning: true } },
        provideRouter(appRoutes, withComponentInputBinding(), withRouterConfig({ onSameUrlNavigation: 'reload' })),
        // metricsInterceptor first: sees the relative API path and times the whole chain, token refreshes included.
        provideHttpClient(withInterceptors([metricsInterceptor, timeoutInterceptor, ...CORE_INTERCEPTORS, apiErrorMessageInterceptor])),
        provideStore({}, { metaReducers: [clearState] }),
        ...DEVTOOLS_PROVIDERS,
        provideEffects(),
        provideCore({ serverUrl: environment.serverUrl + '/api/v1/panel/' }),
        // After provideCore() so the app's HttpService (corelabs client + app fluent methods) wins the HttpClient token.
        {
            provide: HttpClient,
            useClass: HttpService,
        },
        provideAccount({ skip: ['logout-error'] }),
        provideMessages({ secondsShown: 5, visibleCount: 2, updateInterval: 100 }),
        ...provideSessionStore(),
        ...provideIconStore(),
        provideTranslateService({
            loader: { provide: TranslateLoader, useFactory: translateLoaderStatic },
            missingTranslationHandler: { provide: MissingTranslationHandler, useFactory: customMissingTranslationHandler },
        }),
    ],
};
